import { KpiCard, Panel } from "@/lovable-demo/components/bits";
import { EscalationGroup } from "@/lovable-demo/components/escalations";
import {
  fmtDateTime,
  isDueToday,
  isNew,
  isAttention,
  isOpen,
  isPostDecision,
  isRecentlyClosed,
  isOverdue,
  isRecentlyDecided,
  useEscalations,
  type Escalation,
} from "@/lovable-demo/lib/governance/escalations";

function inScope(list: Escalation[], recordIds: Set<string>) {
  return list.filter((e) => recordIds.has(e.record_id));
}

/** Executive Summary — reads the governed record escalations; holds no copy. */
export function ManagementEscalations({ recordIds }: { recordIds: string[] }) {
  const { escalations, loading } = useEscalations();
  const list = inScope(escalations, new Set(recordIds));
  const open = list.filter(isOpen);
  const critical = open.filter((e) => e.severity === "critical" || isOverdue(e));
  const awaiting = open.filter((e) => e.status === "awaiting_decision");
  const recent = list.filter((e) => isRecentlyDecided(e, 14));
  const attention = list.filter(isAttention);
  const followUp = list.filter(isPostDecision);
  return (
    <Panel
      title="Management Escalations"
      subtitle="Decision points raised on governed Problem Records — select a record code to open it"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Open escalations" value={String(open.length)} />
            <KpiCard
              label="Critical / past due"
              value={String(critical.length)}
              tone={critical.length ? "bad" : "good"}
            />
            <KpiCard
              label="Awaiting decision"
              value={String(awaiting.length)}
              tone={awaiting.length ? "warn" : "good"}
            />
            <KpiCard label="Recently decided" value={String(recent.length)} hint="last 14 days" />
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            <EscalationGroup
              title="Critical / past due"
              items={critical}
              empty="Nothing critical or past due."
            />
            <EscalationGroup
              title="Awaiting decision"
              items={awaiting}
              empty="No escalation is awaiting a decision."
            />
          </div>
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-primary">
              All open and recently decided
            </summary>
            <div className="mt-2 grid gap-4 xl:grid-cols-2">
              <EscalationGroup title="Open escalations" items={open} empty="None open." />
              <EscalationGroup
                title="Recently decided"
                items={recent}
                empty="No recent decisions."
              />
              <EscalationGroup
                title="Level 1 · Attention (monitoring)"
                items={attention}
                empty="No attention items."
              />
              <EscalationGroup
                title="Decided — actions / effectiveness pending"
                items={followUp}
                empty="Nothing awaiting actions or effectiveness."
              />
            </div>
          </details>
        </div>
      )}
    </Panel>
  );
}

/** Daily Briefing — what management needs to decide today. */
export function DecisionsRequiringAttention({ recordIds }: { recordIds: string[] }) {
  const { escalations, events, loading } = useEscalations();
  const ids = new Set(recordIds);
  const list = inScope(escalations, ids);
  const overdue = list.filter((e) => isOverdue(e));
  const dueToday = list.filter((e) => isDueToday(e));
  const fresh = list.filter((e) => isNew(e));
  const resolved = list.filter((e) => isRecentlyDecided(e, 3) || isRecentlyClosed(e, 3));
  const attention = list.filter(isAttention);
  const dayAgo = Date.now() - 24 * 3600 * 1000;
  const changes = events
    .filter((ev) => ids.has(ev.record_id) && new Date(ev.occurred_at).getTime() >= dayAgo)
    .reverse();
  return (
    <Panel
      title="Decisions Requiring Attention"
      subtitle="What management needs to decide today — each entry opens its governed Problem Record"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-foreground/90">
            <span className="num font-semibold text-destructive">{overdue.length}</span> overdue and{" "}
            <span className="num font-semibold text-warning">{dueToday.length}</span> due today need
            a management decision.
            {fresh.length > 0 && (
              <>
                {" "}
                {fresh.length} new escalation{fresh.length > 1 ? "s" : ""} arrived in the last 24
                hours.
              </>
            )}
          </p>
          <div className="grid gap-4 xl:grid-cols-2">
            <EscalationGroup
              title="Overdue decisions"
              items={overdue}
              empty="No decision is overdue."
            />
            <EscalationGroup
              title="Decisions due today"
              items={dueToday}
              empty="Nothing is due today."
            />
          </div>
          <EscalationGroup
            title="New escalations"
            items={fresh}
            empty="No new escalations since yesterday."
          />
          {attention.length > 0 && (
            <EscalationGroup
              title="Level 1 · Attention — watch, no decision yet"
              items={attention}
              empty=""
            />
          )}
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-primary">
              Material changes since yesterday ({changes.length}) · Recently resolved (
              {resolved.length})
            </summary>
            <div className="mt-2 grid gap-4 xl:grid-cols-2">
              <div>
                <h3 className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Material changes since yesterday
                </h3>
                {changes.length === 0 ? (
                  <p className="py-2 text-xs text-muted-foreground">
                    No escalation changes in the last 24 hours.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/60 text-xs">
                    {changes.map((c) => (
                      <li key={c.id} className="py-2">
                        <span className="num font-semibold text-primary">{c.record_code}</span>{" "}
                        <span className="capitalize">{c.event_type}</span>{" "}
                        <span className="text-muted-foreground">
                          · {fmtDateTime(c.occurred_at)} · {c.actor_label}
                        </span>
                        {c.detail && <span className="block text-foreground/80">{c.detail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <EscalationGroup
                title="Recently resolved escalations"
                items={resolved}
                empty="Nothing resolved in the last 3 days."
              />
            </div>
          </details>
        </div>
      )}
    </Panel>
  );
}
